'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Button } from './button';

interface PaginationProps {
	currentPage: number;
	totalPages: number;
	preserveParams?: boolean; // Optional flag to preserve other query params
	handlePageChange?: (page: number) => void; // For testing/Storybook
	// Add these optional props to make component testable
	isStorybook?: boolean;
}

export function Pagination({
	currentPage,
	totalPages,
	preserveParams = true, // Default to preserving params
	handlePageChange,
	isStorybook = false,
}: PaginationProps) {
	// Only use Next.js hooks if not in Storybook
	const router = !isStorybook ? useRouter() : null;
	const searchParams = !isStorybook ? useSearchParams() : null;

	const handlePageChangeInternal = (page: number) => {
		// Use the prop if provided or if in Storybook
		if (handlePageChange || isStorybook) {
			handlePageChange?.(page);
			return;
		}

		// Default behavior with router
		const params = new URLSearchParams(searchParams?.toString() || '');
		params.set('page', page.toString());
		const queryString = preserveParams
			? params.toString()
			: new URLSearchParams({ page: page.toString() }).toString();
		router?.push(`?${queryString}`);
	};

	// Only show pagination if we have more than one page
	if (totalPages <= 1) return null;

	// Calculate which page numbers to show
	const getPageNumbers = () => {
		const delta = 2; // Number of pages to show on each side of current page
		const range = [];
		const rangeWithDots = [];
		let l;

		for (let i = 1; i <= totalPages; i++) {
			if (
				i === 1 || // Always show first page
				i === totalPages || // Always show last page
				(i >= currentPage - delta && i <= currentPage + delta) // Show pages around current page
			) {
				range.push(i);
			}
		}

		for (const i of range) {
			if (l) {
				if (i - l === 2) {
					rangeWithDots.push(l + 1);
				} else if (i - l !== 1) {
					rangeWithDots.push('...');
				}
			}
			rangeWithDots.push(i);
			l = i;
		}

		return rangeWithDots;
	};

	return (
		<div className="mt-8 flex items-center justify-center gap-2">
			<Button
				variant="outline"
				size="icon"
				className="pagination-link"
				onClick={() => handlePageChangeInternal(currentPage - 1)}
				disabled={currentPage <= 1}>
				<ChevronLeft className="h-4 w-4" />
			</Button>

			<div className="flex items-center gap-1">
				{getPageNumbers().map((pageNum, i) =>
					pageNum === '...' ? (
						<span key={`dots-${i}`} className="px-2">
							...
						</span>
					) : (
						<Button
							key={`page-${pageNum}`}
							variant={
								currentPage === pageNum ? 'default' : 'outline'
							}
							size="icon"
							className="pagination-link"
							onClick={() =>
								handlePageChangeInternal(pageNum as number)
							}>
							{pageNum}
						</Button>
					),
				)}
			</div>

			<Button
				variant="outline"
				size="icon"
				className="pagination-link"
				onClick={() => handlePageChangeInternal(currentPage + 1)}
				disabled={currentPage >= totalPages}>
				<ChevronRight className="h-4 w-4" />
			</Button>
		</div>
	);
}
